import { IWasmCustomData, TRoutePopupInfoColorVariant } from "~/interfaces";
import { TEXTURES_NAMES, WASM_TEXTURES } from "./const";
import { TUrlProviderNamesVariants } from "./interfaces";
import { isUrlIcon } from "./utils";
import Wasm from "./wasm";
import { createRoutePopupContentLines, getCenteredIcon, getCircle, getCircleImage, getPathCaption, getReview, getRoutePointCircle, getText, getWeatherIconTexture, svgToCanvas } from "./textures-utils";
import { TPositionShareImageVariant } from "./store/interfaces";

export function bindWasmTextureUrlProvider(wasm: Wasm) {
	const mapContent = wasm.mapContent;

	mapContent.setRequestDataCallback(async requestedUrl => {
		let wasHandled = false;
		const correctUrl = requestedUrl.match(/^\/\//u)
			? `https:${requestedUrl}`
			: requestedUrl.match(/^\//u)
				? `${location.origin}${requestedUrl}`
				: requestedUrl;
		const url = new URL(correctUrl);

		switch (url.protocol) {
			case `${TEXTURES_NAMES.main}:`: {
				const textureName = url.host;
				// @ts-ignore
				const textureData = wasm.textures.getWasmTexture(textureName);

				if (textureData) {
					wasHandled = true;
					mapContent.setRequestedData(requestedUrl, textureData.imageData);
				}
				break;
			}

			case `${TEXTURES_NAMES.custom}:`: {
				const host = url.host as TUrlProviderNamesVariants;

				switch (host) {
					case "user-marks": {
						const order = parseFloat(url.searchParams.get("order"));
						const isActive = url.searchParams.get("active") === "1";
						const opacity = parseFloat(url.searchParams.get("opacity"));
						const textureData = wasm.textures.getSmallNumber({
							value: order,
							isActive,
							opacity,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "marker-text": {
						const text = url.searchParams.get("text");
						const textureData = getText(text);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "marker-review": {
						const reviewValue = parseFloat(url.searchParams.get("value"));
						const reviewActive = url.searchParams.get("active") === "1";
						const textureData = getReview(reviewValue, reviewActive);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "circle-radius": {
						const width = parseFloat(url.searchParams.get("width"));
						const color = url.searchParams.get("color");
						const textureData = getCircle(color, width);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "route": {
						const isActive = url.searchParams.get("active") === "1";
						const isEnd = url.searchParams.get("start") === "0";
						const opacity = parseFloat(url.searchParams.get("opacity"));
						const routeStartEndData = wasm.textures.getGetRouteStartEnd({
							isActive,
							isEnd,
							opacity,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, routeStartEndData.imageData);
						break;
					}

					case "position-share-device": {
						const order = parseFloat(url.searchParams.get("order"));
						const isActive = url.searchParams.get("active") === "1";
						const textureData = wasm.textures.getLocationShareDeviceNumber(order, isActive);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					// poloha - obal kolecko + fotka (neaktivni)
					case "position-share-image": {
						const img = url.searchParams.get("img");
						const variant = url.searchParams.get("variant") as TPositionShareImageVariant;
						const textureData = await wasm.textures.getLocationShareImage(img, variant);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "path-caption": {
						const text = url.searchParams.get("text");
						const textureData = getPathCaption(text);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "route-point-circle": {
						const radius = parseFloat(url.searchParams.get("radius"));
						const color = url.searchParams.get("color");
						const textureData = getRoutePointCircle(radius, color);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "circle-image": {
						const size = parseFloat(url.searchParams.get("size"));
						const img = url.searchParams.get("img");
						const iconSize = parseFloat(url.searchParams.get("iconsize"));
						const isIcon = isUrlIcon(img);
						let imageData: ImageData = null;

						if (isIcon) {
							const iconData = await getCenteredIcon(img, size, iconSize);

							imageData = iconData.imageData;
						} else {
							const circleData = await getCircleImage(img, size, false);

							imageData = circleData.imageData;
						}

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, imageData);
						break;
					}

					case "route-popup-info": {
						const jsonStr = url.searchParams.get("json");
						const lines = JSON.parse(jsonStr);
						const variant = url.searchParams.get("variant");
						const linesData = createRoutePopupContentLines(lines, variant as TRoutePopupInfoColorVariant);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-weather": {
						const icon = url.searchParams.get("icon");
						const value = url.searchParams.get("value");
						const time = url.searchParams.get("time");
						const forceFill = url.searchParams.get("forcefill");
						const windAngleParam = url.searchParams.get("windangle");
						const textBold = url.searchParams.get("bold") === "1";
						let iconElem: HTMLCanvasElement = null;

						if (icon) {
							try {
								iconElem = await svgToCanvas(icon, WASM_TEXTURES.ratio, forceFill);
							} catch (exc) {
								/* eslint-disable-next-line */
								console.error(`Wasm - route-popup-weather provider exception ${exc.message}`);
							}
						} else if (windAngleParam) {
							const windAngle = parseFloat(windAngleParam);

							iconElem = wasm.textures.windAngle(windAngle);
						}

						const linesData = getWeatherIconTexture(value, time, textBold, iconElem);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-pubt-summary": {
						const text = url.searchParams.get("text");
						const warning = url.searchParams.get("warning") === "1";
						const icons = JSON.parse(url.searchParams.get("icons")) as IWasmCustomData["routePopupPubtSummary"]["icons"];
						const linesData = wasm.textures.createroutePopupPubtSummary({
							text,
							warning,
							icons,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-pubt-section": {
						const title = url.searchParams.get("title");
						const subTitle = url.searchParams.get("subtitle");
						const desc = url.searchParams.get("desc");
						const titleColor = url.searchParams.get("titlecolor");
						const descColor = url.searchParams.get("desccolor");
						const linesData = wasm.textures.createroutePopupPubtSection({
							title,
							subTitle,
							desc,
							titleColor,
							descColor,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-closure": {
						const icon = url.searchParams.get("icon") as IWasmCustomData["routePopupClosure"]["icon"];
						const text = url.searchParams.get("text");
						const linesData = wasm.textures.createroutePopupClosure({
							icon,
							text,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					default:
				}
				break;
			}

			default: {
				// fetch data
				try {
					const response = await fetch(correctUrl);
					const data = await response.arrayBuffer();

					// set data back to conent
					wasHandled = true;
					mapContent.setRequestedData(requestedUrl, new Uint8Array(data));
				} catch (exc) {
					/* eslint-disable-next-line */
					console.error(`Wasm url provider exception - ${exc.message} for url ${requestedUrl}`);
				}
			}
		}

		/* eslint-disable-next-line */
		!wasHandled && console.log(`Request url for ${requestedUrl} [unhandled]`);
	});
}
